/*============================================================================
  Cart Location Map
  - Delivery location picker: a popup opened when the shopper clicks Checkout
    (cart page + cart drawer), and optionally an inline map on the cart page.
  - The pin is fixed in the centre of the map: the shopper drags the map and
    the coordinates under the pin are saved once the movement stops.
  - Primary engine: Google Maps. Optional fallback (theme setting):
    OpenStreetMap + Leaflet (no key required).
  - Saves the chosen location to cart attributes:
      map_lat, map_lng, map_link,
      map_text (full formatted address), map_street (street + number),
      map_city, map_area (district/sublocality), map_governorate,
      map_country, map_postcode
  - Address search + reverse geocoding (Google Places / Nominatim).
==============================================================================*/
(function () {
  'use strict';

  // The snippet can be rendered twice on the cart page (popup + inline), which
  // would inject this file twice. Run once.
  if (window.__clmLoaded) return;
  window.__clmLoaded = true;

  var GOOGLE_CB = '__cartLocationMapGoogleReady';
  var googleLoadPromise = null;

  // Sections that started with Google, so we can fall back to OSM if the key
  // turns out to be invalid/unauthorized (reported via window.gm_authFailure,
  // NOT via a promise rejection - the script itself loads fine).
  var googleSections = [];

  function round6(n) {
    return Math.round(Number(n) * 1e6) / 1e6;
  }

  // Google/OSM return "Unnamed Road" (or the Arabic equivalent) when a road has
  // no name. That is not a real street name, so we drop it.
  function isUnnamedRoad(s) {
    if (!s) return false;
    var n = String(s).trim().toLowerCase();
    return /unnamed\s*(road|rd|street|st)?/.test(n) || n.indexOf('بدون اسم') !== -1;
  }

  // Local address line derived from a full formatted address: keep the leading
  // segments (block / street / building) and drop the administrative tail we
  // already store separately (city, governorate, country, postal, Plus Code).
  // This preserves Google's own ordering for countries like Kuwait where the
  // "Block X, Street Y, Building Z" detail lives only in the formatted string.
  function localLineFromFormatted(text, dropValues) {
    if (!text) return '';
    var drop = (dropValues || [])
      .filter(Boolean)
      .map(function (s) {
        return String(s).toLowerCase();
      });
    var kept = text
      .split(',')
      .map(function (s) {
        return s.trim();
      })
      .filter(function (seg) {
        if (!seg) return false;
        var lc = seg.toLowerCase();
        if (drop.indexOf(lc) !== -1) return false; // city / governorate / country
        if (isUnnamedRoad(seg)) return false; // "Unnamed Road"
        if (/^[0-9a-z]{4,}\+[0-9a-z]{2,}$/i.test(seg)) return false; // Plus Code
        if (/^\d{4,6}(-\d+)?$/.test(seg)) return false; // bare postal code
        return true;
      });
    return kept.join(', ');
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  // Universal location link (opens in Google Maps on any device,
  // regardless of which map engine was used).
  function buildMapLink(lat, lng) {
    return 'https://www.google.com/maps?q=' + round6(lat) + ',' + round6(lng);
  }

  // Human-readable address for status messages: join the structured parts
  // (street, area, city, governorate, country), de-duped, so we never show a
  // raw Plus Code like "W66C+42" to the shopper. Falls back to the formatted
  // text only when no structured parts are available.
  function humanAddress(addr) {
    if (!addr) return '';
    var out = [];
    [addr.street, addr.area, addr.city, addr.governorate, addr.country].forEach(function (p) {
      if (p && out.indexOf(p) === -1) out.push(p);
    });
    return out.join(', ') || addr.text || '';
  }

  /*----------------------------------------------------------------------------
    Address parsers -> a normalized { text, street, city } object.
    We read structured components (never parse the formatted string), because
    the string format varies by country and often collapses to a Plus Code
    (e.g. "W66C+42 ...") when a place has no formal street address.
  ----------------------------------------------------------------------------*/

  // Google: results[0].address_components (Geocoder) or Place.addressComponents
  // (Places API New). The two shapes differ, so we read both key styles.
  function parseGoogleAddress(components, text) {
    components = components || [];
    function get(type) {
      for (var i = 0; i < components.length; i++) {
        var c = components[i];
        var types = c.types || [];
        if (types.indexOf(type) !== -1) {
          return c.long_name || c.longText || c.short_name || c.shortText || '';
        }
      }
      return '';
    }
    var city =
      get('locality') ||
      get('postal_town') ||
      get('sublocality') ||
      get('administrative_area_level_2');
    // Governorate (Kuwait) maps to the top-level administrative area.
    var governorate = get('administrative_area_level_1');
    if (!city) city = governorate; // last resort so city is never empty
    var country = get('country');
    var postal = get('postal_code');

    // Street name (+ building number). "Unnamed Road" is not a real street name.
    var route = get('route');
    if (isUnnamedRoad(route)) route = '';
    var number = get('street_number') || get('premise') || get('subpremise');
    var streetName = (route + (number ? ' ' + number : '')).trim();
    // Block / neighbourhood. In Kuwait the "Block N" value comes as
    // `neighborhood`; `sublocality_level_1` is the district/area name
    // (e.g. "Saad Al Abdullah"), NOT the block - so check neighborhood first.
    var block =
      get('neighborhood') ||
      get('sublocality_level_2') ||
      get('sublocality_level_1') ||
      get('sublocality');

    // AREA (منطقة) = district within the city, e.g. "Shamiya" = sublocality,
    // NOT the locality ("Al Kuwayt"/"Kuwait City"). `neighborhood` is skipped
    // because that carries "Block N".
    var area =
      get('sublocality_level_1') ||
      get('sublocality') ||
      get('sublocality_level_2');

    // Assemble in the requested order: street, number, then block.
    var street = [streetName, block].filter(Boolean).join(', ');

    // Fallback: derive from the formatted address when components gave us
    // nothing (strip the administrative parts we keep separately).
    if (!street) {
      street = localLineFromFormatted(text, [city, governorate, country, postal]);
    } else if (block && street.toLowerCase().indexOf(block.toLowerCase()) === -1) {
      // Safety net: make sure the block ends up in the line.
      street = street + ', ' + block;
    }
    return {
      text: text || '',
      street: street,
      city: city,
      area: area,
      governorate: governorate,
      country: country,
      postcode: postal
    };
  }

  // Nominatim (OSM): the `address` object returned with addressdetails=1.
  function parseNominatimAddress(a, text) {
    a = a || {};
    var road = a.road || a.pedestrian || a.footway || a.residential || '';
    if (isUnnamedRoad(road)) road = '';
    var number = a.house_number || a.house_name || '';
    // Block / neighbourhood, when present (Kuwait & similar addressing).
    var block = a.city_block || a.block || a.neighbourhood || a.suburb || a.quarter || '';
    var street = [block, (road + (number ? ' ' + number : '')).trim()]
      .filter(Boolean)
      .join(', ');
    var city = a.city || a.town || a.village || a.municipality || a.county || '';
    // Area / district (OSM equivalent of Google's sublocality).
    var area = a.suburb || a.city_district || a.district || a.neighbourhood || a.quarter || '';
    var governorate = a.state || a.governorate || a.region || a.province || '';
    var country = a.country || '';
    var postcode = a.postcode || '';
    return {
      text: text || '',
      street: street,
      city: city,
      area: area,
      governorate: governorate,
      country: country,
      postcode: postcode
    };
  }

  /*----------------------------------------------------------------------------
    Save to cart attributes.
    Posts all attributes atomically to /cart/update.js.
    `addr` is a normalized { text, street, city }; fields may be empty when
    coordinates are saved before reverse geocoding resolves, or when a place
    has no street (e.g. Plus Code locations).
  ----------------------------------------------------------------------------*/
  function saveCartAttributes(lat, lng, addr) {
    addr = addr || {};
    var attrs = {
      map_lat: String(round6(lat)),
      map_lng: String(round6(lng)),
      map_link: buildMapLink(lat, lng),
      map_text: addr.text || '',
      map_street: addr.street || '',
      map_city: addr.city || '',
      map_area: addr.area || '',
      map_governorate: addr.governorate || '',
      map_country: addr.country || '',
      map_postcode: addr.postcode || ''
    };

    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.dispatchEvent(
          new CustomEvent('cart:location-updated', { detail: { cart: cart, attrs: attrs } })
        );
        return cart;
      });
  }

  /*----------------------------------------------------------------------------
    Load the Google Maps JS API (once per page).
  ----------------------------------------------------------------------------*/
  function loadGoogle(apiKey, language, useSearch) {
    if (window.google && window.google.maps) return Promise.resolve(window.google);
    if (googleLoadPromise) return googleLoadPromise;

    googleLoadPromise = new Promise(function (resolve, reject) {
      window[GOOGLE_CB] = function () {
        resolve(window.google);
      };
      var libs = useSearch ? '&libraries=places' : '';
      var s = document.createElement('script');
      s.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(apiKey) +
        libs +
        '&language=' +
        encodeURIComponent(language || 'en') +
        '&loading=async&callback=' +
        GOOGLE_CB;
      s.async = true;
      s.defer = true;
      s.onerror = function () {
        reject(new Error('google-load-failed'));
      };
      document.head.appendChild(s);
    });
    return googleLoadPromise;
  }

  /*----------------------------------------------------------------------------
    Load Leaflet (OpenStreetMap fallback).
  ----------------------------------------------------------------------------*/
  var leafletPromise = null;
  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;

    leafletPromise = new Promise(function (resolve, reject) {
      var css = document.createElement('link');
      css.rel = 'stylesheet';
      css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(css);

      var s = document.createElement('script');
      s.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      s.async = true;
      s.onload = function () {
        resolve(window.L);
      };
      s.onerror = function () {
        reject(new Error('leaflet-load-failed'));
      };
      document.head.appendChild(s);
    });
    return leafletPromise;
  }

  /*==========================================================================
    Per-section controller.
  ==========================================================================*/
  function initSection(root) {
    if (!root) return null;
    if (root.dataset.clmInit === 'true') return root.__clmApi || null;
    root.dataset.clmInit = 'true';

    var cfg = {
      apiKey: root.dataset.apiKey || '',
      allowOsm: root.dataset.allowOsm === 'true',
      enableSearch: root.dataset.enableSearch === 'true',
      lang: root.dataset.lang || 'en',
      isRtl: root.dataset.rtl === 'true',
      zoom: parseInt(root.dataset.zoom, 10) || 12
    };

    // Starting point priority:
    //   1. map_* attributes       -> the shopper already picked a spot
    //   2. geo_latitude/geo_longitude -> auto-detected geolocation (geo-permission)
    //   3. section default
    var savedLat = parseFloat(root.dataset.savedLat);
    var savedLng = parseFloat(root.dataset.savedLng);
    var hasSaved = !isNaN(savedLat) && !isNaN(savedLng);

    var geoLat = parseFloat(root.dataset.geoLat);
    var geoLng = parseFloat(root.dataset.geoLng);
    var hasGeo = !isNaN(geoLat) && !isNaN(geoLng);

    // Latest known detected location (may arrive live via the geo:updated event).
    var latestGeo = hasGeo ? { lat: geoLat, lng: geoLng } : null;
    // True once the shopper has explicitly picked a spot (map_* attributes).
    var addressSelected = hasSaved;

    var center;
    if (hasSaved) {
      center = { lat: savedLat, lng: savedLng };
    } else if (hasGeo) {
      center = { lat: geoLat, lng: geoLng };
    } else {
      center = {
        lat: parseFloat(root.dataset.defaultLat) || 29.3759,
        lng: parseFloat(root.dataset.defaultLng) || 47.9774
      };
    }

    var t = {};
    try {
      t = JSON.parse(root.dataset.texts || '{}');
    } catch (e) {
      t = {};
    }

    var els = {
      map: root.querySelector('[data-clm-map]'),
      status: root.querySelector('[data-clm-status]'),
      locate: root.querySelector('[data-clm-locate]'),
      search: root.querySelector('[data-clm-search]'),
      suggestions: root.querySelector('[data-clm-suggestions]'),
      coords: root.querySelector('[data-clm-coords]'),
      debug: {
        city: root.querySelector('[data-clm-debug-city]'),
        street: root.querySelector('[data-clm-debug-street]'),
        area: root.querySelector('[data-clm-debug-area]'),
        governorate: root.querySelector('[data-clm-debug-governorate]'),
        country: root.querySelector('[data-clm-debug-country]'),
        postcode: root.querySelector('[data-clm-debug-postcode]'),
        text: root.querySelector('[data-clm-debug-text]')
      }
    };

    // Debug panel (only present when the "Show address debug panel" setting is on).
    function renderDebug(addr) {
      addr = addr || {};
      function set(node, val) {
        if (node) node.textContent = val || '—'; // em dash when empty
      }
      set(els.debug.city, addr.city);
      set(els.debug.street, addr.street);
      set(els.debug.area, addr.area);
      set(els.debug.governorate, addr.governorate);
      set(els.debug.country, addr.country);
      set(els.debug.postcode, addr.postcode);
      set(els.debug.text, addr.text);
    }

    function setStatus(msg, type) {
      if (!els.status) return;
      els.status.textContent = msg || '';
      els.status.setAttribute('data-type', type || 'info');
    }

    // Reveal the locate button once the map is actually loaded.
    function markReady() {
      root.classList.add('clm-ready');
    }

    // Move the map to the detected location, but only while the shopper
    // hasn't picked a spot yet. Skipped when the initial center already used it.
    function applyPendingGeo() {
      if (addressSelected || !controller || !latestGeo || hasGeo) return;
      controller.setView(latestGeo.lat, latestGeo.lng, true);
    }

    // Detected location can arrive live (geo-permission asks on page load).
    document.addEventListener('geo:updated', function (e) {
      var d = e && e.detail;
      if (!d) return;
      var la = parseFloat(d.lat);
      var ln = parseFloat(d.lng);
      if (isNaN(la) || isNaN(ln)) return;
      latestGeo = { lat: la, lng: ln };
      applyPendingGeo();
    });

    function setCoordsLabel(lat, lng) {
      if (els.coords) {
        els.coords.textContent = round6(lat) + ', ' + round6(lng);
      }
    }

    // Resolves once the latest save request has settled - the popup waits for
    // it before letting the shopper continue to checkout.
    var pendingSave = Promise.resolve();
    // Reverse geocoding lands a moment after the coordinates, in a second
    // write. Tracked separately so the popup can wait for the address too.
    var pendingAddress = Promise.resolve();

    // `start(done)` runs the reverse geocode + save and calls done() when it
    // has been written. Capped, so a slow geocoder never blocks checkout.
    function trackAddress(start) {
      pendingAddress = new Promise(function (resolve) {
        var settled = false;
        function finish() {
          if (settled) return;
          settled = true;
          resolve();
        }
        setTimeout(finish, 3000);
        start(finish);
      });
    }

    function persist(lat, lng, addr) {
      addressSelected = true; // the shopper committed a location
      setCoordsLabel(lat, lng);
      if (addr) renderDebug(addr); // keep prior values on the coords-only pre-save
      pendingSave = saveCartAttributes(lat, lng, addr)
        .then(function () {
          setStatus(t.saved || 'Location saved.', 'success');
        })
        .catch(function () {
          setStatus(t.saveError || 'Could not save the location. Please try again.', 'error');
        });
      return pendingSave;
    }

    // Shared controller each engine provides:
    //   setPosition(lat,lng,pan) - move there and save
    //   setView(lat,lng,zoomIn)  - move there without saving
    //   commitCenter()           - save whatever is under the pin right now
    //   refresh()                - recalculate size (popup re-open)
    var controller = null;

    /* ---------------- Google Maps ---------------- */
    function startGoogle(google) {
      var map = new google.maps.Map(els.map, {
        center: center,
        zoom: cfg.zoom,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        clickableIcons: false,
        gestureHandling: 'greedy',
        keyboardShortcuts: false,
        cameraControl: false,
        rotateControl: false,
        zoomControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM }
      });

      var geocoder = new google.maps.Geocoder();

      // Last position we saved (or moved to programmatically). The `idle`
      // handler compares the map centre against it so a camera move we
      // triggered ourselves is not saved twice.
      var last = { lat: round6(center.lat), lng: round6(center.lng) };

      function commit(lat, lng, reverse) {
        last = { lat: round6(lat), lng: round6(lng) };
        persist(lat, lng); // save coords right away; address filled in below
        if (reverse) {
          trackAddress(function (done) {
            reverseGeocodeGoogle(geocoder, lat, lng, function (addr) {
              persist(lat, lng, addr).then(done, done); // same spot + address
            });
          });
        }
      }

      function commitCenter() {
        var c = map.getCenter();
        if (c) commit(c.lat(), c.lng(), true);
      }

      map.addListener('dragstart', function () {
        root.classList.add('clm-dragging');
      });

      // Fires once the camera settles after any move (drag, zoom, pan).
      map.addListener('idle', function () {
        root.classList.remove('clm-dragging');
        var c = map.getCenter();
        if (!c) return;
        if (round6(c.lat()) === last.lat && round6(c.lng()) === last.lng) return;
        commit(c.lat(), c.lng(), true);
      });

      // Tapping the map pans it, so the pin ends up on the tapped spot.
      map.addListener('click', function (e) {
        map.panTo(e.latLng);
      });

      // Search: Google Places API (New) - AutocompleteSuggestion data API,
      // rendered into our own suggestion list (legacy Autocomplete widget
      // requires the deprecated "Places API" and is avoided on purpose).
      if (cfg.enableSearch && els.search && els.suggestions && google.maps.places) {
        setupGoogleSearch(google, function (lat, lng, addr) {
          last = { lat: round6(lat), lng: round6(lng) };
          map.setCenter({ lat: lat, lng: lng });
          map.setZoom(Math.max(map.getZoom(), 15));
          persist(lat, lng, addr); // the picked prediction already has the address
          var human = humanAddress(addr);
          if (human) setStatus((t.selected || 'Selected location:') + ' ' + human, 'success');
        });
      }

      controller = {
        setPosition: function (lat, lng, pan) {
          if (pan) {
            map.setCenter({ lat: lat, lng: lng });
            map.setZoom(Math.max(map.getZoom(), 15));
          }
          commit(lat, lng, true);
        },
        // Move the map without saving (used for detected geolocation).
        setView: function (lat, lng, zoomIn) {
          last = { lat: round6(lat), lng: round6(lng) };
          map.setCenter({ lat: lat, lng: lng });
          if (zoomIn) map.setZoom(Math.max(map.getZoom(), 15));
        },
        commitCenter: commitCenter,
        refresh: function () {
          var c = map.getCenter();
          google.maps.event.trigger(map, 'resize');
          if (c) map.setCenter(c);
        }
      };

      if (hasSaved) setCoordsLabel(center.lat, center.lng);
      applyPendingGeo();
      google.maps.event.addListenerOnce(map, 'idle', markReady);
    }

    function reverseGeocodeGoogle(geocoder, lat, lng, cb) {
      geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, st) {
        if (st === 'OK' && results && results[0]) {
          var r = results[0];
          // A reverse geocode returns several results at different granularity;
          // the block/street can live in a result other than [0]. Merge all
          // components (de-duped) so the parser can find them wherever they are.
          var merged = [];
          var seen = {};
          results.forEach(function (x) {
            (x.address_components || []).forEach(function (c) {
              var key = (c.types || []).join('|') + '::' + (c.long_name || '');
              if (!seen[key]) {
                seen[key] = 1;
                merged.push(c);
              }
            });
          });
          var addr = parseGoogleAddress(merged, r.formatted_address);
          setStatus((t.selected || 'Selected location:') + ' ' + humanAddress(addr), 'success');
          if (cb) cb(addr);
        }
      });
    }

    // Address search using Places API (New): fetch predictions, render our own
    // dropdown, and resolve the picked place to coordinates.
    function setupGoogleSearch(google, onPick) {
      var box = els.suggestions;
      var Suggestion = google.maps.places.AutocompleteSuggestion;
      var SessionToken = google.maps.places.AutocompleteSessionToken;
      if (!Suggestion || !SessionToken) return; // Places (New) not available

      var token = new SessionToken();

      function clear() {
        box.innerHTML = '';
        box.hidden = true;
      }

      var run = debounce(function () {
        var q = els.search.value.trim();
        if (q.length < 3) {
          clear();
          return;
        }
        Suggestion.fetchAutocompleteSuggestions({ input: q, sessionToken: token })
          .then(function (res) {
            var list = (res && res.suggestions) || [];
            box.innerHTML = '';
            var rendered = 0;
            list.forEach(function (sug) {
              var pred = sug.placePrediction;
              if (!pred) return;
              var li = document.createElement('li');
              li.className = 'clm__suggestion';
              li.textContent = pred.text && pred.text.text ? pred.text.text : '';
              li.addEventListener('click', function () {
                var picked = li.textContent;
                els.search.value = picked;
                clear();
                var place = pred.toPlace();
                place
                  .fetchFields({ fields: ['location', 'formattedAddress', 'addressComponents'] })
                  .then(function () {
                    var loc = place.location;
                    if (loc) {
                      var addr = parseGoogleAddress(
                        place.addressComponents,
                        place.formattedAddress || picked
                      );
                      onPick(loc.lat(), loc.lng(), addr);
                    }
                    token = new SessionToken(); // start a new billing session
                  })
                  .catch(function () {});
              });
              box.appendChild(li);
              rendered++;
            });
            box.hidden = rendered === 0;
          })
          .catch(clear);
      }, 350);

      els.search.addEventListener('input', run);
      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) clear();
      });
    }

    /* ---------------- OpenStreetMap / Leaflet ---------------- */
    function startLeaflet(L) {
      var map = L.map(els.map, { zoomControl: false }).setView([center.lat, center.lng], cfg.zoom);
      L.control.zoom({ position: 'bottomright' }).addTo(map);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: 'Map data (c) OpenStreetMap contributors'
      }).addTo(map);

      // No marker object: the pin is a fixed HTML overlay in the middle of the
      // map, so what gets saved is always the map centre.
      var last = { lat: round6(center.lat), lng: round6(center.lng) };

      function commit(lat, lng, reverse) {
        last = { lat: round6(lat), lng: round6(lng) };
        persist(lat, lng); // save coords right away; address filled in below
        if (reverse) {
          trackAddress(function (done) {
            reverseGeocodeNominatim(lat, lng, function (addr) {
              persist(lat, lng, addr).then(done, done); // same spot + address
            });
          });
        }
      }

      function commitCenter() {
        var c = map.getCenter();
        if (c) commit(c.lat, c.lng, true);
      }

      map.on('movestart', function () {
        root.classList.add('clm-dragging');
      });

      map.on('moveend', function () {
        root.classList.remove('clm-dragging');
        var c = map.getCenter();
        if (!c) return;
        if (round6(c.lat) === last.lat && round6(c.lng) === last.lng) return;
        commit(c.lat, c.lng, true);
      });

      // Tapping the map pans it, so the pin ends up on the tapped spot.
      map.on('click', function (e) {
        map.panTo(e.latlng);
      });

      // Search: Nominatim (OpenStreetMap).
      if (cfg.enableSearch && els.search && els.suggestions) {
        setupNominatimSearch(function (lat, lng, addr) {
          last = { lat: round6(lat), lng: round6(lng) };
          map.setView([lat, lng], Math.max(map.getZoom(), 15));
          persist(lat, lng, addr); // the picked result already has the address
          var human = humanAddress(addr);
          if (human) setStatus((t.selected || 'Selected location:') + ' ' + human, 'success');
        });
      }

      controller = {
        setPosition: function (lat, lng, pan) {
          last = { lat: round6(lat), lng: round6(lng) };
          if (pan) map.setView([lat, lng], Math.max(map.getZoom(), 15));
          commit(lat, lng, true);
        },
        // Move the map without saving (used for detected geolocation).
        setView: function (lat, lng, zoomIn) {
          last = { lat: round6(lat), lng: round6(lng) };
          map.setView([lat, lng], zoomIn ? Math.max(map.getZoom(), 15) : map.getZoom());
        },
        commitCenter: commitCenter,
        refresh: function () {
          map.invalidateSize();
        }
      };

      // Leaflet needs a size recalculation if the container was hidden.
      setTimeout(function () {
        map.invalidateSize();
      }, 200);

      if (hasSaved) setCoordsLabel(center.lat, center.lng);
      applyPendingGeo();
      map.whenReady(markReady);
    }

    function reverseGeocodeNominatim(lat, lng, cb) {
      fetch(
        'https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=' +
          lat +
          '&lon=' +
          lng +
          '&accept-language=' +
          cfg.lang
      )
        .then(function (r) {
          return r.json();
        })
        .then(function (d) {
          if (d && d.display_name) {
            var addr = parseNominatimAddress(d.address, d.display_name);
            setStatus((t.selected || 'Selected location:') + ' ' + humanAddress(addr), 'success');
            if (cb) cb(addr);
          }
        })
        .catch(function () {});
    }

    function setupNominatimSearch(onPick) {
      var box = els.suggestions;
      function clear() {
        box.innerHTML = '';
        box.hidden = true;
      }
      var run = debounce(function () {
        var q = els.search.value.trim();
        if (q.length < 3) {
          clear();
          return;
        }
        fetch(
          'https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&accept-language=' +
            cfg.lang +
            '&q=' +
            encodeURIComponent(q)
        )
          .then(function (r) {
            return r.json();
          })
          .then(function (list) {
            box.innerHTML = '';
            if (!list || !list.length) {
              clear();
              return;
            }
            list.forEach(function (item) {
              var li = document.createElement('li');
              li.className = 'clm__suggestion';
              li.textContent = item.display_name;
              li.addEventListener('click', function () {
                els.search.value = item.display_name;
                clear();
                onPick(
                  parseFloat(item.lat),
                  parseFloat(item.lon),
                  parseNominatimAddress(item.address, item.display_name)
                );
              });
              box.appendChild(li);
            });
            box.hidden = false;
          })
          .catch(clear);
      }, 400);
      els.search.addEventListener('input', run);
      document.addEventListener('click', function (e) {
        if (!root.contains(e.target)) clear();
      });
    }

    /* ---------------- "Use my location" button ---------------- */
    if (els.locate) {
      els.locate.addEventListener('click', function () {
        if (!navigator.geolocation) {
          setStatus(t.geoUnsupported || 'Geolocation is not supported by your browser.', 'error');
          return;
        }
        setStatus(t.locating || 'Detecting your location...', 'info');
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            if (controller) controller.setPosition(pos.coords.latitude, pos.coords.longitude, true);
          },
          function () {
            setStatus(t.geoDenied || 'Could not get your location. Please allow location access.', 'error');
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
        );
      });
    }

    /* ---------------- Engine selection + fallback ---------------- */
    function fallbackOrFail(reason) {
      if (cfg.allowOsm) {
        loadLeaflet()
          .then(startLeaflet)
          .catch(function () {
            setStatus(t.mapError || 'Map could not be loaded.', 'error');
          });
      } else {
        setStatus(reason, 'error');
      }
    }

    // Called when Google reports an auth failure (invalid key, referer not
    // allowed, API not enabled). Tear down the broken Google map and use OSM.
    function switchToOsm() {
      if (!cfg.allowOsm || root.dataset.clmOsm === 'true') return;
      root.dataset.clmOsm = 'true';
      if (els.map) els.map.innerHTML = '';
      controller = null;
      loadLeaflet()
        .then(startLeaflet)
        .catch(function () {
          setStatus(t.mapError || 'Map could not be loaded.', 'error');
        });
    }

    if (cfg.apiKey) {
      googleSections.push(switchToOsm);
      loadGoogle(cfg.apiKey, cfg.lang, cfg.enableSearch)
        .then(startGoogle)
        .catch(function () {
          fallbackOrFail(t.mapError || 'Map could not be loaded.');
        });
    } else if (cfg.allowOsm) {
      loadLeaflet()
        .then(startLeaflet)
        .catch(function () {
          setStatus(t.mapError || 'Map could not be loaded.', 'error');
        });
    } else {
      setStatus(t.noApiKey || 'Google Maps API key is not configured.', 'error');
    }

    root.__clmApi = {
      // Make sure something is saved before continuing to checkout: if the
      // shopper never moved the map, take whatever sits under the pin.
      ensureSaved: function () {
        if (!addressSelected && controller && controller.commitCenter) {
          controller.commitCenter();
        }
        return pendingSave.then(function () {
          return pendingAddress;
        });
      },
      refresh: function () {
        if (controller && controller.refresh) controller.refresh();
      }
    };
    return root.__clmApi;
  }

  /*==========================================================================
    Checkout popup: intercept the checkout click, show the map, continue on
    confirm. Closing the popup (X / Esc / backdrop) cancels and stays put.
  ==========================================================================*/
  var modalApi = null;
  var checkoutBypass = false; // guards the synthetic re-click
  var confirmedForms = []; // forms whose location has already been confirmed

  function setupModal(modal) {
    if (modal.dataset.clmModalInit === 'true') return;
    modal.dataset.clmModalInit = 'true';

    // Move the popup to the end of <body> so no parent stacking context
    // (drawer, sticky bars) can clip it.
    if (modal.parentNode !== document.body) document.body.appendChild(modal);

    var inner = modal.querySelector('[data-cart-location-map]');
    var confirmBtn = modal.querySelector('[data-clm-confirm]');
    var api = null;
    var onConfirmed = null;

    function onKeydown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') close();
    }

    function open(done) {
      onConfirmed = done || null;
      modal.hidden = false;
      document.documentElement.classList.add('clm-modal-open');
      void modal.offsetWidth; // reflow, so the open transition runs
      modal.classList.add('clm-modal--open');
      document.addEventListener('keydown', onKeydown);
      // The map is only built the first time the popup is opened, so pages
      // that never reach checkout do not load the Maps API at all.
      if (!api) {
        api = initSection(inner);
      } else {
        api.refresh();
      }
    }

    function close() {
      onConfirmed = null;
      modal.classList.remove('clm-modal--open');
      document.documentElement.classList.remove('clm-modal-open');
      document.removeEventListener('keydown', onKeydown);
      setTimeout(function () {
        if (!modal.classList.contains('clm-modal--open')) modal.hidden = true;
      }, 250);
    }

    modal.querySelectorAll('[data-clm-dismiss]').forEach(function (el) {
      el.addEventListener('click', close);
    });

    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        var done = onConfirmed;
        confirmBtn.disabled = true;
        var wait = api && api.ensureSaved ? api.ensureSaved() : Promise.resolve();
        Promise.resolve(wait)
          .catch(function () {})
          .then(function () {
            confirmBtn.disabled = false;
            close();
            if (done) done();
          });
      });
    }

    modalApi = { open: open, close: close };
  }

  // Capture phase: run before the theme's own cart form handlers.
  document.addEventListener(
    'click',
    function (e) {
      if (checkoutBypass || !modalApi) return;
      var target = e.target;
      if (!target || !target.closest) return;
      var btn = target.closest('button[name="checkout"], input[name="checkout"]');
      if (!btn || btn.disabled) return;

      var form = btn.form || btn.closest('form');
      if (form && confirmedForms.indexOf(form) !== -1) return; // already confirmed
      // Let the theme's terms-and-conditions guard speak first.
      var terms = form && form.querySelector('.cart__terms-checkbox');
      if (terms && !terms.checked) return;

      e.preventDefault();
      e.stopPropagation();

      modalApi.open(function () {
        if (form) confirmedForms.push(form);
        checkoutBypass = true;
        if (btn.isConnected) {
          btn.click(); // synchronous: re-enters this handler and passes through
        } else if (form) {
          form.submit(); // the cart was re-rendered while the popup was open
        }
        checkoutBypass = false;
      });
    },
    true
  );

  function initAll(scope) {
    var el = scope || document;
    el.querySelectorAll('[data-cart-location-map]').forEach(function (root) {
      if (root.dataset.lazy === 'true') return; // popup maps build on first open
      initSection(root);
    });
    el.querySelectorAll('[data-clm-modal]').forEach(setupModal);
  }

  // Hook Google's auth-failure callback once, preserving any existing handler.
  if (!window.__clmAuthHooked) {
    window.__clmAuthHooked = true;
    var prevAuthFailure = window.gm_authFailure;
    window.gm_authFailure = function () {
      if (typeof prevAuthFailure === 'function') prevAuthFailure();
      googleSections.forEach(function (toOsm) {
        toOsm();
      });
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // Shopify theme editor support - re-init when the section reloads.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
